import { computed, effect, inject, untracked } from '@angular/core';

import { withDevtools } from '@angular-architects/ngrx-toolkit';
import {
  AppError,
  SpinnerService,
  ToastService,
  ToastType,
} from '@hglobal/fin-ui';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap } from 'rxjs';

import { Currency, Operation, UserOperationType } from '@iw/core/entities';
import { getDateFromSeconds } from '@iw/core/helpers';
import { DictionariesService } from '@iw/core/services';
import { ShellStore } from '@iw/shell';
import {
  DictionariesStore,
  setErrorStatusFor,
  setPendingStatusFor,
  setSuccessStatusFor,
  StoreHelperService,
  withFiltersUpdate,
  withRequestHandler,
  withRequestStatus,
} from '@iw/store';

import { ORDERS_HISTORY_API } from './api';
import { OrdersFilters, OrdersHistoryState } from './models';
import { Comment } from './models/get-order-res.model';

export const initialFilters: OrdersFilters = {
  cursor: null,
  limit: 50,
  status: null,
  type: null,
  accountIds: null,
  dateFrom: null,
  dateTo: null,
  account: null,
  category: null,
};

const initialState: OrdersHistoryState = {
  orderRes: null,
  ordersRes: null,
  ongoingOrdersRes: null,
  filters: initialFilters,
  ongoingFilters: initialFilters,
};

export const OrdersHistoryStore = signalStore(
  withDevtools('ordersHistory'),
  withState(initialState),
  withFiltersUpdate<OrdersFilters>(),
  withRequestStatus([
    'getOrders',
    'getOngoingOrders',
    'getOrder',
    'cancelOrder',
    'sendComment',
  ]),
  withRequestHandler(),
  withProps((store) => ({
    store,
    toast: inject(ToastService),
    shellStore: inject(ShellStore),
    ordersApi: inject(ORDERS_HISTORY_API),
    spinnerService: inject(SpinnerService),
    dictionariesStore: inject(DictionariesStore),
    storeHelperService: inject(StoreHelperService),
    dictionariesService: inject(DictionariesService),
  })),
  withComputed(
    ({
      dictionariesService,
      filters,
      ongoingFilters,
      shellStore,
      store,
      dictionariesStore,
    }) => ({
      finalStatuses: computed(() =>
        dictionariesService.operationFinalStatuses.map((item) => item.id),
      ),
      ongoingStatuses: computed(() =>
        dictionariesService.operationOngoingStatuses.map((item) => item.id),
      ),
      accountsCurrencies: computed(() => {
        return shellStore
          .fiatAccounts()
          .map(({ currency, id }) => ({ name: currency.symbol, id }));
      }),
      accountIds: computed(() => {
        const { account } = filters();
        return shellStore
          .fiatAccounts()
          .filter((item) => !account || item.id === account)
          .map((account) => account.id);
      }),
      ongoingAccountIds: computed(() => {
        const { account } = ongoingFilters();
        return shellStore
          .fiatAccounts()
          .filter((item) => !account || item.id === account)
          .map((account) => account.id);
      }),
      orders: computed(() => {
        const state = store.ordersRes();
        if (!state) return state;

        return {
          ...state,
          items: attachCurrencies(state.items, (symbol) =>
            dictionariesStore.getCurrency(symbol, 'symbol'),
          ),
        };
      }),
      ongoingOrders: computed(() => {
        const state = store.ongoingOrdersRes();
        if (!state) return state;

        return {
          ...state,
          items: attachCurrencies(state.items, (symbol) =>
            dictionariesStore.getCurrency(symbol, 'symbol'),
          ),
        };
      }),

      order: computed(() => {
        const state = store.orderRes();
        if (!state) return state;

        return {
          ...state,
          item: {
            ...state.item,
            currency: dictionariesStore.getCurrency(
              state.item.details.currency,
              'symbol',
            ),
          },
          order: {
            ...state.order,
            currency: dictionariesStore.getCurrency(
              state.order.currency as unknown as string,
              'symbol',
            ),
          },
        };
      }),
    }),
  ),
  withMethods(({ store }) => ({
    update: (payload: Partial<OrdersHistoryState>) =>
      patchState(store, payload),
  })),
  withMethods(
    ({
      store,
      shellStore,
      ordersApi,
      finalStatuses,
      ongoingStatuses,
      accountIds,
      ongoingAccountIds,
      update,
      orderRes,
      toast,
    }) => ({
      getOrders: rxMethod<OrdersFilters>(
        switchMap((params) => {
          patchState(store, setPendingStatusFor('getOrders'));

          const request = {
            ...params,
            accountIds: accountIds(),
            type: UserOperationType.Withdrawal,
            dateFrom: getDateFromSeconds(params.dateFrom),
            dateTo: getDateFromSeconds(params.dateTo ?? params.dateFrom, true),
            statuses: !params.status ? finalStatuses() : [params.status],
          };

          return ordersApi.getOrders(request).pipe(
            tapResponse({
              next: (res) => {
                patchState(
                  store,
                  {
                    ordersRes: params.cursor
                      ? {
                          items: [...store.ordersRes().items, ...res.items],
                          count: res.count,
                          cursor: res.cursor,
                        }
                      : res,
                  },
                  setSuccessStatusFor('getOrders'),
                );
              },
              error: (error: AppError) =>
                patchState(store, setErrorStatusFor('getOrders', error)),
            }),
          );
        }),
      ),
      getOngoingOrders: rxMethod<OrdersFilters>(
        switchMap((params) => {
          patchState(store, setPendingStatusFor('getOngoingOrders'));

          const request = {
            ...params,
            accountIds: ongoingAccountIds(),
            type: UserOperationType.Withdrawal,
            dateFrom: getDateFromSeconds(params.dateFrom),
            dateTo: getDateFromSeconds(params.dateTo ?? params.dateFrom, true),
            statuses: !params.status ? ongoingStatuses() : [params.status],
          };

          return ordersApi.getOrders(request).pipe(
            tapResponse({
              next: (res) => {
                patchState(
                  store,
                  {
                    ongoingOrdersRes: params.cursor
                      ? {
                          items: [
                            ...store.ongoingOrdersRes().items,
                            ...res.items,
                          ],
                          count: res.count,
                          cursor: res.cursor,
                        }
                      : res,
                  },
                  setSuccessStatusFor('getOngoingOrders'),
                );
              },
              error: (error: AppError) =>
                patchState(store, setErrorStatusFor('getOngoingOrders', error)),
            }),
          );
        }),
      ),
      getOrder: rxMethod<{ id: string }>(
        switchMap((payload) => {
          patchState(store, setPendingStatusFor('getOrder'));
          return ordersApi.getOrder(payload).pipe(
            tapResponse({
              next: (orderRes) => {
                patchState(
                  store,
                  { orderRes },
                  setSuccessStatusFor('getOrder'),
                );
              },
              error: (error: AppError) =>
                patchState(store, setErrorStatusFor('getOrder', error)),
            }),
          );
        }),
      ),
      cancelOrder: rxMethod<{ id: string }>(
        switchMap((payload) => {
          patchState(store, setPendingStatusFor('cancelOrder'));
          return ordersApi.cancelOrder(payload).pipe(
            tapResponse({
              next: () => {
                patchState(store, setSuccessStatusFor('cancelOrder'));
                shellStore.handleOperationCompleted(payload.id);
              },
              error: (error: AppError) => {
                patchState(store, setErrorStatusFor('cancelOrder', error));
                toast.show({ type: ToastType.Error, message: error.message });
              },
            }),
          );
        }),
      ),
      sendComment: rxMethod<Comment>(
        switchMap((payload) => {
          const orderValue = orderRes();
          patchState(
            store,
            {
              orderRes: {
                ...orderValue,
                comments: [...orderValue.comments, payload],
              },
            },
            setPendingStatusFor('sendComment'),
          );
          return ordersApi
            .sendComment({
              operationId: orderValue.item.id,
              comment: payload.message,
            })
            .pipe(
              tapResponse({
                next: () => {
                  patchState(store, setSuccessStatusFor('sendComment'));
                },
                error: (error: AppError) => {
                  patchState(store, setErrorStatusFor('sendComment', error));
                  toast.show({ type: ToastType.Error, message: error.message });
                },
              }),
            );
        }),
      ),
      refreshOrders: () => {
        update({
          filters: { ...store.filters(), cursor: null },
          ordersRes: null,
        });
      },
      refreshOngoingOrders: () => {
        update({
          ongoingFilters: { ...store.ongoingFilters(), cursor: null },
          ongoingOrdersRes: null,
        });
      },
    }),
  ),
  withHooks({
    onInit(store) {
      store.getOngoingOrders(store.ongoingFilters);
      store.getOrders(store.filters);

      effect(() => {
        if (store.shellStore.operationCompleted()) {
          untracked(() => {
            store.refreshOrders();
            store.refreshOngoingOrders();
          });
        }
      });

      effect(() => {
        if (store.error()) {
          untracked(() => store.storeHelperService.handleError(store.error()));
        }
      });
    },
  }),
);

function attachCurrencies(
  items: Operation[],
  getCurrency: (symbol: string) => Currency,
): Operation[] {
  if (!items) return [];

  return items.map((item) => {
    const isExchange = item.type === UserOperationType.Exchange;

    return {
      ...item,
      currency: getCurrency(item.details.currency),
      ...(isExchange && {
        targetCurrency: getCurrency(item.details.targetCurrency),
      }),
    };
  });
}
